import React, { useState, useEffect } from 'react';

export default function UserManagement() {
  const [admins,     setAdmins]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);
  const [form, setForm] = useState({
    username: '', email: '', full_name: '', role: 'SUBUSER'
  });

  const token   = localStorage.getItem('nb_admin_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const res  = await fetch('/api/admin/users', { headers });
      const data = await res.json();
      if (data.success) setAdmins(data.users);
    } catch { showToast('Failed to load users.', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAdmins(); }, []);

  const handleCreate = async () => {
    if (!form.username || !form.email || !form.full_name) {
      return showToast('Username, email and full name are required.', 'error');
    }
    setSubmitting(true);
    try {
      const res  = await fetch('/api/admin/users/create', { method: 'POST', headers, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) {
        showToast(`Admin user "${form.username}" created successfully!`);
        setForm({ username: '', email: '', full_name: '', role: 'SUBUSER' });
        setShowForm(false);
        loadAdmins();
      } else {
        showToast(data.error || 'Failed to create user.', 'error');
      }
    } catch { showToast('Failed to create user.', 'error'); }
    finally { setSubmitting(false); }
  };

  const handleToggle = async (adminId, current) => {
    try {
      const res  = await fetch('/api/admin/users/toggle', { method: 'POST', headers, body: JSON.stringify({ admin_id: adminId, is_active: !current }) });
      const data = await res.json();
      if (data.success) { showToast(`User ${!current ? 'activated' : 'deactivated'}`); loadAdmins(); }
    } catch { showToast('Failed to update.', 'error'); }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 7,
    border: '0.5px solid #e2e8f0', background: '#f8fafc',
    color: '#0f172a', fontSize: 13, outline: 'none',
    boxSizing: 'border-box', fontFamily: "'DM Sans',-apple-system,sans-serif"
  };

  const labelStyle = {
    fontSize: 10, color: '#64748b', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.4,
    display: 'block', marginBottom: 5
  };

  // Role display helper — 3-way now that Dealer Admin exists. Kept as a single
  // source of truth so the stat cards, table badge and role-info card can't
  // drift out of sync the way the old inline 2-way ternary would have if left
  // as-is (it would have silently mislabeled DEALER_ADMIN as "Sub User").
  const roleLabel = (role) => role === 'ADMIN' ? 'Full Admin' : role === 'DEALER_ADMIN' ? 'Dealer Admin' : 'Sub User';
  const roleColors = (role) => role === 'ADMIN'
    ? { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' }
    : role === 'DEALER_ADMIN'
      ? { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' }
      : { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' };

  const fullAdmins  = admins.filter(a => a.role === 'ADMIN').length;
  const dealerAdmins= admins.filter(a => a.role === 'DEALER_ADMIN').length;
  const subUsers    = admins.filter(a => a.role === 'SUBUSER').length;
  const active      = admins.filter(a => a.is_active).length;

  return (
    <div style={{ fontFamily: "'DM Sans',-apple-system,sans-serif" }}>

      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4', border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: toast.type === 'error' ? '#dc2626' : '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>{toast.msg}</div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Users',   value: admins.length, color: '#1d4ed8' },
          { label: 'Full Admins',   value: fullAdmins,    color: '#15803d' },
          { label: 'Dealer Admins', value: dealerAdmins,  color: '#c2410c' },
          { label: 'Sub Users',     value: subUsers,      color: '#7c3aed' },
          { label: 'Active Users',  value: active,        color: '#c2410c' },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', border: `0.5px solid ${'#e2e8f0'}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 600, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Role Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
        {[
          { role: 'ADMIN', label: 'Full Admin', desc: 'Can enable/disable segments, send alerts, generate files, manage users and dealers. Full access to all modules.', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
          { role: 'SUBUSER', label: 'Sub User', desc: 'RMS team access: Sq-Off Orders (including file generation/download), Sync Monitor, Order Reports, and Client Upload only. Cannot manage users, enable segments, send alerts, or access Dealer Access / Audit Logs / System Logs.', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
          { role: 'DEALER_ADMIN', label: 'Dealer Admin', desc: 'Support-desk role. Can view Sq-Off Orders, Order Reports, Dealer Access, Audit Logs and System Logs only. File generation/download is fully disabled -- blocked in the UI and rejected by the server.', color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' },
        ].map(r => (
          <div key={r.role} style={{ background: r.bg, border: `0.5px solid ${r.border}`, borderRadius: 10, padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: r.color, background: '#fff', padding: '2px 10px', borderRadius: 10, border: `0.5px solid ${r.border}` }}>{r.label}</span>
            </div>
            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>{r.desc}</div>
          </div>
        ))}
      </div>

      {/* Create Form */}
      {showForm && (
        <div style={{ background: '#fff', border: '0.5px solid #bfdbfe', borderRadius: 10, padding: '24px', marginBottom: 20, boxShadow: '0 4px 16px rgba(37,99,235,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Create New Admin User</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>User will login via username + OTP sent to their email</div>
            </div>
            <button onClick={() => { setShowForm(false); setForm({ username: '', email: '', full_name: '', role: 'SUBUSER' }); }}
              style={{ background: 'transparent', border: '0.5px solid #e2e8f0', color: '#64748b', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
              ✕ Cancel
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Username *</label>
              <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase() }))}
                placeholder="e.g. rmsuser1" style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#1d4ed8'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Full name" style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#1d4ed8'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
            <div>
              <label style={labelStyle}>Email ID *</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="user@navia.co.in" style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#1d4ed8'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
            <div>
              <label style={labelStyle}>Role *</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="SUBUSER">Sub User</option>
                <option value="ADMIN">Full Admin</option>
                <option value="DEALER_ADMIN">Dealer Admin</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleCreate} disabled={submitting} style={{
              padding: '9px 24px', borderRadius: 7, border: 'none',
              background: submitting ? '#93c5fd' : '#1d4ed8', color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(29,78,216,0.2)'
            }}>
              {submitting ? 'Creating...' : '+ Create User'}
            </button>
            <button onClick={() => setForm({ username: '', email: '', full_name: '', role: 'SUBUSER' })}
              style={{ padding: '9px 20px', borderRadius: 7, border: '0.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div style={{ background: '#fff', border: `0.5px solid ${'#e2e8f0'}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Admin Users</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Manage RMS admin and sub-user access</div>
          </div>
          <button onClick={() => setShowForm(!showForm)} style={{
            padding: '7px 16px', borderRadius: 6, border: 'none',
            background: showForm ? '#f1f5f9' : '#1d4ed8',
            color: showForm ? '#64748b' : '#fff',
            fontSize: 12, fontWeight: 500, cursor: 'pointer'
          }}>
            {showForm ? '✕ Close' : '+ Add User'}
          </button>
        </div>

        {/* Table Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '60px 120px 1fr 200px 80px 160px 160px 140px', padding: '10px 20px', background: '#f8fafc', borderBottom: `0.5px solid ${'#e2e8f0'}` }}>
          {['#', 'Username', 'Full Name', 'Email', 'Role', 'Created On', 'Last Login', 'Status'].map(h => (
            <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Loading users...</div>
        ) : admins.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No users found.</div>
        ) : admins.map((u, i) => (
          <div key={u.admin_id} style={{
            display: 'grid', gridTemplateColumns: '60px 120px 1fr 200px 80px 160px 160px 140px',
            padding: '13px 20px', alignItems: 'center',
            borderBottom: i < admins.length - 1 ? '0.5px solid #f1f5f9' : 'none',
            background: u.is_active ? '#fff' : '#fafafa'
          }}>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>#{u.admin_id}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{u.username}</div>
            <div style={{ fontSize: 13, color: '#374151' }}>{u.full_name}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
            <div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                background: roleColors(u.role).bg,
                color:      roleColors(u.role).color,
                border:     `0.5px solid ${roleColors(u.role).border}`
              }}>
                {roleLabel(u.role)}
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {u.created_at ? new Date(u.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
            </div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {u.last_login ? new Date(u.last_login).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                background: u.is_active ? '#f0fdf4' : '#fff5f5',
                color:      u.is_active ? '#15803d' : '#dc2626',
                border:     `0.5px solid ${u.is_active ? '#bbf7d0' : '#fecaca'}`
              }}>
                {u.is_active ? 'Active' : 'Inactive'}
              </span>
              <button onClick={() => handleToggle(u.admin_id, u.is_active)} style={{
                padding: '3px 10px', borderRadius: 5, border: '0.5px solid #e2e8f0',
                background: '#fff', color: '#64748b', fontSize: 10, cursor: 'pointer'
              }}>
                {u.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}